// SPDX-License-Identifier: AGPL-3.0-or-later

import channelItemStyles from '@app/features/app/components/layout/ChannelItem.module.css';
import {ChannelItemContent} from '@app/features/app/components/layout/ChannelItemContent';
import {GenericChannelItem} from '@app/features/app/components/layout/GenericChannelItem';
import styles from '@app/features/table_game_leaderboard/components/GuildTableGameLeaderboardRow.module.css';
import TableGameLeaderboards from '@app/features/table_game_leaderboard/state/TableGameLeaderboards';
import {
	GUILD_WEEK_ROW_DESCRIPTOR,
	MY_RANK_DESCRIPTOR,
	MY_WEEK_PLACE_DESCRIPTOR,
} from '@app/features/table_game_leaderboard/utils/TableGameLeaderboardMessages';
import {isTableGameRankedEnabled} from '@app/features/table_game_leaderboard/utils/TableGameRankedFlag';
import {remFromPx} from '@app/features/theme/layout/RemFromPx';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {useLingui} from '@lingui/react/macro';
import {TrophyIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';

function openWeeklyBoard(guildId: string): void {
	void import('@app/features/table_game_leaderboard/components/TableGameLeaderboardModal').then(
		({TableGameLeaderboardModal}) =>
			ModalCommands.push(
				ModalCommands.modal(() => (
					<TableGameLeaderboardModal guildId={guildId} data-flx="table-game-leaderboard.guild-row.modal" />
				)),
			),
	);
}

// The community's weekly board, pinned above the channels in the members row's pattern. It sends no request: my place
// shows once this session has seen it on the board (TableGameLeaderboards.guildWeekRanks).
export const GuildTableGameLeaderboardRow = observer(function GuildTableGameLeaderboardRow({
	guildId,
}: {
	guildId: string;
}) {
	const {i18n} = useLingui();
	if (!isTableGameRankedEnabled()) return null;
	const rank = TableGameLeaderboards.getGuildWeekRank(guildId);
	const name = i18n._(GUILD_WEEK_ROW_DESCRIPTOR);
	return (
		<div className={styles.section} data-flx="table-game-leaderboard.guild-row.section">
			<GenericChannelItem
				containerClassName={channelItemStyles.container}
				className={clsx(
					channelItemStyles.channelItem,
					channelItemStyles.channelItemRegular,
					channelItemStyles.channelItemHoverable,
				)}
				aria-label={rank !== null ? `${name}, ${i18n._(MY_WEEK_PLACE_DESCRIPTOR, {rank})}` : name}
				onClick={() => openWeeklyBoard(guildId)}
				data-flx="table-game-leaderboard.guild-row.item"
			>
				<ChannelItemContent
					icon={
						<TrophyIcon
							size={remFromPx(20)}
							className={clsx(channelItemStyles.channelItemIcon, channelItemStyles.channelItemIconUnselected)}
							data-flx="table-game-leaderboard.guild-row.icon"
						/>
					}
					name={name}
					actions={
						rank !== null && (
							<span className={styles.rank} aria-hidden data-flx="table-game-leaderboard.guild-row.rank">
								{i18n._(MY_RANK_DESCRIPTOR, {rank})}
							</span>
						)
					}
					data-flx="table-game-leaderboard.guild-row.content"
				/>
			</GenericChannelItem>
		</div>
	);
});
